'use client';

import { ReactNode } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';
import { useStore } from '@/lib/store';

interface CollapsibleSectionProps {
  /** Stable key used to persist open/collapsed state across reloads. */
  sectionKey: string;
  /** Section title (e.g. "TV Shows: Recently Added"). */
  title: string;
  /** Lucide icon component shown in the header pill. */
  icon: LucideIcon;
  /** Tint of the icon pill. Most sections use red (brand); a few use neutral. */
  iconTone?: 'red' | 'neutral';
  /** Item count rendered in a glass pill next to the title. */
  count: number;
  /** Singular/plural label for the count (e.g. "shows", "movies", "titles"). */
  countLabel?: { singular: string; plural: string };
  /** Optional right-side slot (e.g. a "Mark all watched" affordance). */
  rightSlot?: ReactNode;
  children: ReactNode;
}

export function CollapsibleSection({
  sectionKey,
  title,
  icon: Icon,
  iconTone = 'red',
  count,
  countLabel = { singular: 'item', plural: 'items' },
  rightSlot,
  children,
}: CollapsibleSectionProps) {
  const collapsed = useStore((s) => s.collapsedSections[sectionKey] === true);
  const toggle = useStore((s) => s.toggleSectionCollapsed);

  const iconWrapClass =
    iconTone === 'red'
      ? 'bg-red-500/10 ring-1 ring-red-500/25'
      : 'bg-white/10 ring-1 ring-white/15';
  const iconClass = iconTone === 'red' ? 'text-red-500' : 'text-gray-300';

  return (
    <section className="space-y-5">
      <div className="group/header flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => toggle(sectionKey)}
          aria-expanded={!collapsed}
          aria-controls={`section-${sectionKey}`}
          className="flex-1 min-w-0 flex items-center gap-3 py-2 -mx-1 px-1 rounded-xl hover:bg-white/[0.04] active:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60"
        >
          <span className={`flex items-center justify-center h-10 w-10 rounded-xl shrink-0 ${iconWrapClass}`}>
            <Icon className={`h-5 w-5 ${iconClass}`} />
          </span>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white truncate">
            {title}
          </h2>
          <span className="shrink-0 inline-flex items-center rounded-full bg-white/10 ring-1 ring-white/15 px-2.5 py-1 text-xs font-medium text-gray-300 backdrop-blur-sm">
            {count} {count === 1 ? countLabel.singular : countLabel.plural}
          </span>
          <ChevronDown
            className={`ml-auto h-5 w-5 text-gray-400 shrink-0 transition-transform duration-200 group-hover/header:text-white ${
              collapsed ? '-rotate-90' : ''
            }`}
          />
        </button>
        {rightSlot && <div className="shrink-0">{rightSlot}</div>}
      </div>
      {!collapsed && (
        <div id={`section-${sectionKey}`}>
          {children}
        </div>
      )}
    </section>
  );
}
